import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { dataAPI } from '../../helpers/api_service';
import AddIcon from '@mui/icons-material/Add';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Warning as WarningIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import ProjectForm from './ProjectForm';
import ProjectComponent from './ProjectComponent';
import ProjectsListView from './ProjectsList';
import AlertDialog from '../Common/AlertDialog';
import emptyDocIcon from '../../assets/empty_doc.svg';
import './Projects.css';

const Projects = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const { hasAddPermission, hasEditPermission, hasDeletePermission } = usePermissions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // Default to 'active' tab
  const [viewMode, setViewMode] = useState('list');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch projects when component mounts or when activeTab changes
  useEffect(() => {
    fetchProjects();
  }, [activeTab]);

  const fetchProjects = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await dataAPI.getProjectsList({ project_filter: activeTab });
      console.log('Projects fetched successfully:', response);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(error.message || 'Failed to fetch projects. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = () => {
    console.log('Create project button clicked');
    setIsEditMode(false);
    setEditingProject(null);
    setIsFormOpen(true);
  };

  const handleEditProject = (project) => {
    console.log('Edit project:', project);
    setIsEditMode(true);
    setEditingProject(project);
    setIsFormOpen(true);
  };

  const handleDeleteProject = (project) => {
    console.log('Delete project:', project);
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleProjectRowClick = (project) => {
    navigate(`/project/${project.id}/tasks`);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    
    // Clear any existing delete error and success messages
    setDeleteError('');
    setDeleteSuccess('');
    
    try {
      const response = await dataAPI.deleteProject(projectToDelete.id);
      console.log('Delete project response:', response);
      
      // Check if response has status: 'error' even if HTTP status is 200
      if (response && response.status === 'error') {
        const errorMessage = response.message || `Failed to delete project "${projectToDelete?.title || projectToDelete?.name}". Please try again.`;
        setDeleteError(errorMessage);
        // Clear error message after 5 seconds
        setTimeout(() => {
          setDeleteError('');
        }, 5000);
        // Close the delete dialog
        setDeleteDialogOpen(false);
        setProjectToDelete(null);
        return;
      }
      
      // Success case - show success message
      const successMessage = response?.message || `Project "${projectToDelete?.title || projectToDelete?.name}" deleted successfully.`;
      setDeleteSuccess(successMessage);
      // Clear success message after 5 seconds
      setTimeout(() => {
        setDeleteSuccess('');
      }, 5000);
      
      // Refresh the projects list
      fetchProjects();
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      console.error('Error deleting project:', error);
      // Show custom toast error instead of browser alert
      const errorMessage = error.message || `Failed to delete project "${projectToDelete?.title || projectToDelete?.name}". Please try again.`;
      setDeleteError(errorMessage);
      // Clear error message after 5 seconds
      setTimeout(() => {
        setDeleteError('');
      }, 5000);
      // Close the delete dialog
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setIsEditMode(false);
    setEditingProject(null);
  };

  const handleFormSubmit = (response) => {
    console.log(isEditMode ? 'Project updated:' : 'Project created:', response);
    setIsFormOpen(false);
    setIsEditMode(false);
    setEditingProject(null);
    // Refresh the projects list
    fetchProjects();
  };

  const canAddProject = useMemo(() => hasAddPermission('task'), [hasAddPermission]);
  const canEditProject = useMemo(() => hasEditPermission('task'), [hasEditPermission]);
  const canDeleteProject = useMemo(() => hasDeletePermission('task'), [hasDeletePermission]);

  const renderTabs = () => (
    <div
      className="projects-tabs"
      style={{
        display: 'flex',
        gap: '0',
        borderBottom: `2px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
      }}
    >
      <button
        onClick={() => setActiveTab('active')}
        style={{
          padding: '12px 24px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: '500',
          color: activeTab === 'active' ? '#26C3C0' : (mode === 'dark' ? '#999' : '#666'),
          borderBottom: activeTab === 'active' ? '2px solid #26C3C0' : '2px solid transparent',
          fontFamily: 'Urbanist, Arial, sans-serif',
          transition: 'all 0.2s ease',
          marginBottom: '-2px'
        }}
      >
        Active
      </button>
      <button
        onClick={() => setActiveTab('completed')}
        style={{
          padding: '12px 24px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: '500',
          color: activeTab === 'completed' ? '#26C3C0' : (mode === 'dark' ? '#999' : '#666'),
          borderBottom: activeTab === 'completed' ? '2px solid #26C3C0' : '2px solid transparent',
          fontFamily: 'Urbanist, Arial, sans-serif',
          transition: 'all 0.2s ease',
          marginBottom: '-2px'
        }}
      >
        Completed
      </button>
    </div>
  );

  const renderViewToggle = () => (
    <div
      className="projects-view-toggle"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}
    >
      <button
        type="button"
        aria-label={viewMode === 'grid' ? 'Switch to list view' : 'Switch to card view'}
        onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#d9d9d9'}`,
          backgroundColor: mode === 'dark' ? '#121212' : '#f5f7f7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#26C3C0',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        {viewMode === 'grid' ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}
      </button>
      {canAddProject && (
        <button 
          onClick={handleCreateProject}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#26C3C0',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            fontFamily: 'Urbanist, Arial, sans-serif',
            transition: 'background-color 0.2s ease',
            boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#22B0AD';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#26C3C0';
          }}
        >
          <AddIcon style={{ fontSize: '20px' }} />
          Create Project
        </button>
      )}
    </div>
  );

  const renderHeader = () => (
    <div
      className="projects-header"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}
    >
      {renderTabs()}
      {renderViewToggle()}
    </div>
  );

  if (isLoading) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          fontSize: '18px',
          color: mode === 'dark' ? '#FFFFFF' : '#222222',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          Loading projects...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '18px',
          color: '#f44336',
          marginBottom: '16px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {error}
        </div>
        <button 
          onClick={fetchProjects}
          style={{
            background: '#26C3C0',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            fontFamily: 'Urbanist, Arial, sans-serif',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#22B0AD';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#26C3C0';
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (projects.length === 0) {
    // Empty Projects State - similar to DocumentFolders design
    return (
      <>
        <div
          className="projects-page"
          style={{
            padding: '24px',
            backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
            minHeight: '100vh'
          }}
        >
          {renderHeader()}

          {/* Empty State Content */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            paddingTop: '80px'
          }}>
            {/* Empty Projects Icon */}
            <div style={{
              marginBottom: '24px'
            }}>
              <img 
                src={emptyDocIcon} 
                alt="No Projects" 
                style={{
                  width: '71px',
                  height: '59px',
                  filter: mode === 'dark' ? 'brightness(1)' : 'brightness(0.8)'
                }}
              />
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              margin: '0 0 16px 0',
              fontFamily: 'Urbanist, Arial, sans-serif',
              lineHeight: '1.2'
            }}>
              {activeTab === 'active' ? 'No Active Projects' : 'No Completed Projects'}
            </h1>

            {/* Description */}
            <p style={{
              fontSize: '16px',
              color: mode === 'dark' ? '#FFFFFF' : '#666666',
              margin: '0 0 32px 0',
              maxWidth: '600px',
              lineHeight: '1.5',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              {activeTab === 'active' 
                ? 'Get started by creating your first project to organize and track your work. Projects help you manage tasks, timelines, and team collaboration.'
                : 'No completed projects found. Completed projects will appear here once they are marked as complete.'}
            </p>

            {/* Create Project Button */}
            {canAddProject && activeTab === 'active' && (
              <button 
                onClick={handleCreateProject}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#26C3C0',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  transition: 'background-color 0.2s ease',
                  boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#22B0AD';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#26C3C0';
                }}
              >
                <AddIcon style={{ fontSize: '20px' }} />
                Create Project
              </button>
            )}
          </div>
        </div>

        {/* Project Form - Always render this so form can open */}
        <ProjectForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          isEditMode={isEditMode}
          project={editingProject}
        />

        {/* Alert Dialog for deletion confirmation */}
        <AlertDialog
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDeleteProject}
          type="delete"
          title="Confirm Deletion"
          message={`Are you sure you want to delete "${projectToDelete?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Delete Error Toast */}
        {deleteError && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#1a0000',
            border: '1px solid #ff4444',
            borderRadius: '8px',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)',
            zIndex: 2000,
            minWidth: '350px',
            maxWidth: '400px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <WarningIcon style={{
                color: '#ff4444',
                fontSize: '20px'
              }} />
              <span style={{
                color: '#ffffff',
                fontSize: '14px',
                fontFamily: 'Urbanist, Arial, sans-serif',
                lineHeight: '1.4'
              }}>
                {deleteError}
              </span>
            </div>
          </div>
        )}

        {/* Delete Success Toast */}
        {deleteSuccess && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#001a00',
            border: '1px solid #44ff44',
            borderRadius: '8px',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(68, 255, 68, 0.2)',
            zIndex: 2000,
            minWidth: '350px',
            maxWidth: '400px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <CheckCircleIcon style={{
                color: '#44ff44',
                fontSize: '20px'
              }} />
              <span style={{
                color: '#ffffff',
                fontSize: '14px',
                fontFamily: 'Urbanist, Arial, sans-serif',
                lineHeight: '1.4'
              }}>
                {deleteSuccess}
              </span>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="projects-page"
      style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh'
      }}
    >
      {renderHeader()}

      {viewMode === 'grid' ? (
        <div
          className="projects-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: '20px',
            width: '100%'
          }}
        >
          {projects.map((project) => (
            <ProjectComponent
              key={project.id}
              project={project}
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
            />
          ))}
        </div>
      ) : (
        <ProjectsListView
          projects={projects}
          mode={mode}
          onRowClick={handleProjectRowClick}
          onEditProject={handleEditProject}
          onDeleteProject={handleDeleteProject}
          hasEditPermission={canEditProject}
          hasDeletePermission={canDeleteProject}
        />
      )}

      {/* Project Form */}
      <ProjectForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        project={editingProject}
      />

      {/* Alert Dialog for deletion confirmation */}
      <AlertDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDeleteProject}
        type="delete"
        title="Confirm Deletion"
        message={`Are you sure you want to delete "${projectToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Delete Error Toast */}
      {deleteError && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#1a0000',
          border: '1px solid #ff4444',
          borderRadius: '8px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)',
          zIndex: 2000,
          minWidth: '350px',
          maxWidth: '400px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <WarningIcon style={{
              color: '#ff4444',
              fontSize: '20px'
            }} />
            <span style={{
              color: '#ffffff',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              lineHeight: '1.4'
            }}>
              {deleteError}
            </span>
          </div>
        </div>
      )}

      {/* Delete Success Toast */}
      {deleteSuccess && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#001a00',
          border: '1px solid #44ff44',
          borderRadius: '8px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(68, 255, 68, 0.2)',
          zIndex: 2000,
          minWidth: '350px',
          maxWidth: '400px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <CheckCircleIcon style={{
              color: '#44ff44',
              fontSize: '20px'
            }} />
            <span style={{
              color: '#ffffff',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              lineHeight: '1.4'
            }}>
              {deleteSuccess}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
